import { Input } from "antd";
import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import { showConfirmModal } from "components/Modal/showConfirmModal";
import { Markdown } from "components/Form/components/Markdown/Markdown";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { BlockingNavigationModal } from "components/Prompt/BlockingNavigationModal";
import { NAVIGATION_PRIORITIES } from "components/Prompt/NavigationPromptContext";
import { SpinnerAnimation } from "components/SpinnerAnimation/SpinnerAnimation";
import { TimeAgoWithDateTimeFormatter } from "components/TimeAgo/TimeAgoWithDateTimeFormatter";
import { DocsInfoTooltip } from "components/Tooltip/DocsInfoTooltip";
import { FeConfigType } from "config/config";
import { getUserData } from "containers/Auth/Auth.selectors";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { useMigrateOnSaveParams } from "containers/Resources/screens/ResourcesSingle/useCanCommitOnSave";
import { Location } from "history";
import {
    MouseEvent as ReactMouseEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { actions as dataStoreAction } from "utils/DataStore/DataStore.reducer";
import strings from "utils/strings";
import { getIri, getSearchObj } from "utils/utils";
import {
    computeCommentScrollDelta,
    computeCommentTopAlignDelta,
    getScrollParent,
    isFullyWithinBand,
} from "./commentScroll";
import {
    firstUnreadCommentId,
    isReadByMe as isCommentReadByMe,
    readOverride,
    resolveOverrideAction,
    unreadDotA11yProps,
} from "./commentRead";
import {
    canEditComment,
    canSaveEdit,
    editorActionOnComposerFocus,
    hasUnsavedEdit,
    resolvePendingEditTransition,
} from "./commentEdit";
import { buildDeleteCommentConfirm } from "./commentDelete";
import { readToggleAction } from "./commentActionIcons";
import { canToggleResolve, resolveToggleAction } from "./commentResolve";
import { buildDiscardCommentConfirm, shouldConfirmDiscardDraft } from "./commentCancel";
import {
    commentDraftKey,
    pruneCommentDrafts,
    readCommentDraft,
    writeCommentDraft,
} from "./commentDraftStore";
import { CommentFullscreenModal } from "./CommentFullscreenModal";
import { isExpandableComment } from "./commentExpand";
import { buildCommentDeepLinkPath, shouldScrollInPage } from "./commentPermalink";
import {
    createDeepLinkSettleState,
    stepDeepLinkSettle,
    shouldGestureStopSettle,
} from "./commentDeepLinkSettle";
import {
    assigneeBadgeVariant,
    resolveAssigneeName,
    buildAssigneeOptions,
    buildAssigneeNameMap,
    AssigneeUserRow,
} from "./commentAssignee";
import { isComposerLocked, isComposerOpen } from "./commentComposerLayout";
import { CommentAssigneeControl } from "./CommentAssigneeControl";
import styles from "./CommentsPanel.module.scss";

/**
 * One row returned by the generic `record_comment_plain_list` GraphQL query.
 *
 * Typed locally rather than importing from `graphql/generated` because the
 * generated types are (re)produced by the build-time codegen against the
 * deployed backend schema; this keeps local typecheck green while the panel
 * is authored ahead of the first regeneration.
 */
interface RecordCommentRow {
    id: string;
    body: string;
    author_identity_id: string | null;
    author_identity_id_identity: { name: string } | null;
    assignee_identity_id: string | null;
    assignee_identity_id_identity: { name: string } | null;
    parent_comment_id: string | null;
    resolved_at: string | null;
    resolved_by: string | null;
    created_at: string;
    /**
     * When the comment body was last edited, server-stamped on a body update
     * (null while the comment has never been edited since it was posted). Drives
     * the subtle "edited" marker in the comment header.
     */
    edited_at: string | null;
    /**
     * The current identity's sparse read-OVERRIDE row(s) for this comment. The
     * `record_comment_read` list select fails closed to the caller's OWN rows
     * (see resources/record_comment_read.py get_select_list_where), so this
     * reverse-list only ever contains the viewing identity's override. At most
     * one row exists per (comment, identity) (UNIQUE constraint). `is_read`
     * carries the override direction (false = re-flagged unread below the
     * watermark; true = read ahead of the watermark). Absent when the comment's
     * read state follows the record-level watermark — see commentRead.ts.
     */
    record_comment_read_plain_list_record_comment_id: { id: string; is_read: boolean }[] | null;
}

interface CommentsPanelProps {
    id: string;
    isDisabled: boolean;
    /**
     * The record, or its project or bundle, is read-only. The backend refuses
     * every comment change on it, so the panel hides posting, replying,
     * editing, assigning, resolving and deleting. Reading, read/unread marks
     * and permalinks stay available: they change nothing on the record.
     */
    isReadOnly?: boolean;
    setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
    resourceName?: FeConfigType;
    /**
     * Bumped by the parent record screen's manual Refresh. Added to the comment
     * fetch effect deps so a record refresh also re-fetches the comment list,
     * keeping it in sync with the comments-tab badge count (which rides the
     * record body re-fetch). See operator-task-fe-record-refresh-stale-comments.
     */
    refreshNonce?: number;
    /**
     * Same-record / in-page navigation escape hatch, forwarded to the pending-
     * comment navigation guard. Lets same-record URL canonicalizations (the
     * `history.replace` that strips a sub-page flag) through without prompting,
     * so the guard fires only on genuine navigation away — reusing the exact
     * predicate the parent record form uses (see isSameRecordViewNavigation in
     * ResourcesSingle) rather than a parallel check.
     */
    allowNavigation?: (nextLocation: Location) => boolean;
    /**
     * A comment id supplied by a deep-link (`?comment=<id>` on the record URL,
     * see commentPermalink.ts). When set, the panel anchors to that specific
     * comment on open — top-aligned + highlighted, revealing a hidden older
     * thread if needed — instead of the first-unread/newest default; if the id
     * is not in the fetched thread it surfaces a "comment not found" hint. The
     * parent captures it once (and strips it from the URL) so it is stable.
     */
    deepLinkCommentId?: string;
}

/**
 * Long-comment-list readability (operator-task-fe-record-comments-display-long-list-readability).
 * Comments are kept in their chronological (oldest→newest) reading order — the
 * app-wide convention shared with the wake-snapshot comment tail, the
 * `get_record` `comments` key and the docs — while the two long-thread pains are
 * addressed at the view layer (Stefan's chosen option A, 2026-08-26):
 *  - INITIAL_VISIBLE_COMMENTS bounds the initial DOM render regardless of thread
 *    length; older comments are revealed on demand via a "Show older comments"
 *    affordance (OLDER_PAGE_SIZE at a time).
 *  - the newest comment (bottom of the chronological list, next to the composer)
 *    is scrolled into view on load and after posting, so the most-important
 *    comment is visible without a long manual scroll.
 * This is deliberately a pure view-layer bound rather than server-side offset
 * paging: it preserves the exact full-list fetch the comments-tab badge count
 * rides on (no count regression) and the stated pain is render height / scroll
 * position, not fetch size. Server-side paging can be layered on later if
 * threads ever grow to thousands.
 */
const INITIAL_VISIBLE_COMMENTS = 10;
const OLDER_PAGE_SIZE = 20;
// After an armed scroll-to-newest, keep re-anchoring the newest comment as the
// list's height settles (async markdown / syntax highlighting / images grow
// comments AFTER the first frame). Bounded so we only correct during a brief
// settle window and never fight a user who scrolls away afterwards.
const SCROLL_SETTLE_WINDOW_MS = 1500;
// A deep-link (?comment=<id>) opens the record cold, so SIBLING content above
// the comments inside the SAME record scroll container keeps growing well after
// the comment list itself has settled — most notably the record's value
// schema-form, which on a large custom object renders from a short skeleton to
// a tall form several seconds in and shoves the comments far down the scroller,
// and its Monaco editors, which on mount reset the scroller to the top. The
// record scroll container runs with `overflow-anchor: none` (Main.scss, to break
// a header-collapse feedback loop), so the browser does NOT hold the comment
// visually fixed while that content above it grows.
//
// So the deep-link arm does NOT seat the comment early and then re-anchor it as
// the form displaces it: that chase read as the comment flashing into view,
// being pushed back out by the late form render, then slowly re-scrolling back
// (operator-task-fe-deep-link-to-comment, Stefan 2026-10-08). Instead it WAITS
// for the layout to go quiescent, then scrolls the comment into view exactly
// once. Quiescence is detected by POLLING the scroll container's content height
// (not a ResizeObserver — a Monaco-editor-mount scroll reset can carry no size
// change an observer would miss): once the height has stopped changing for
// DEEPLINK_SETTLE_QUIET_MS the late growth is done. A hard cap bounds the wait so
// it can never run unbounded, and a genuine user scroll gesture abandons it.
//
// The quiet window must outlast the GROWTH PAUSES between late Monaco-editor
// mounts on very tall records: a short window let the wait end inside such a
// pause, after which the remaining form growth would have stranded the comment
// far below the fold on the single scroll (observed on a ~13 000px CO with ~10
// editors). Gating on sustained height-stability closes that gap; the user sees
// the record load, then one clean scroll to the comment, with no chasing.
const DEEPLINK_SETTLE_POLL_MS = 120;
const DEEPLINK_SETTLE_QUIET_MS = 2000;
const DEEPLINK_SETTLE_HARD_CAP_MS = 20000;

/**
 * Reply-threading (operator-task-record-comments-ui-enhancements). Replies are
 * grouped under their parent via `record_comment.parent_comment_id` and rendered
 * indented. The DATA nesting is unbounded (a reply can target any comment), but
 * the VISUAL indent is capped at this depth so a long reply chain cannot walk
 * the thread off the right edge — replies deeper than the cap keep rendering at
 * the cap's indent level.
 */
const MAX_THREAD_DEPTH = 4;

const authorLabel = (comment: RecordCommentRow, currentIdentityId?: string): string => {
    const authorIdentityId = comment.author_identity_id;
    if (!authorIdentityId) {
        return "System";
    }
    if (currentIdentityId && authorIdentityId === currentIdentityId) {
        return "You";
    }
    // Prefer the resolved identity name; fall back to a short id when the
    // relationship is unreadable (RBAC) or absent.
    const resolvedName = comment.author_identity_id_identity?.name;
    if (resolvedName) {
        return resolvedName;
    }
    return `Identity ${authorIdentityId.slice(0, 8)}`;
};

export const CommentsPanel = ({
    id,
    isDisabled,
    isReadOnly = false,
    setHasUnsavedChanges,
    resourceName,
    refreshNonce,
    allowNavigation,
    deepLinkCommentId,
}: CommentsPanelProps) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const key = `recordCommentList-${id}`;
    // Separate dataStore key for the viewer's read-until watermark for this
    // record (≤1 row, fail-closed to the caller). Fetched alongside the comment
    // list; see the two-layer read model in commentRead.ts.
    const watermarkKey = `recordCommentWatermark-${id}`;

    const [isLoading, setLoading] = useState(false);
    // The viewing identity's read-until watermark for this record (ISO/space
    // timestamp), and the id of that watermark row (to update vs create it).
    // null until fetched / when the viewer has never marked anything read here.
    const [readUntil, setReadUntil] = useState<string | null>(null);
    const [watermarkId, setWatermarkId] = useState<string | null>(null);
    // Dedicated in-flight flag for the *post comment* request, kept separate
    // from the panel-wide isLoading (list fetch / delete) so the submit button
    // reflects submission state precisely. Drives both the button's pending
    // affordance (spinner) and its disabled state, and guards postComment
    // against re-entry (double-click / future Ctrl+Enter) posting a duplicate.
    const [isSubmitting, setSubmitting] = useState(false);
    // Lock the whole composer (not just the submit button) while posting.
    const composerLocked = isComposerLocked(isDisabled, isSubmitting);
    const [draft, setDraft] = useState("");
    // The composer draft is also kept in localStorage (commentDraftStore.ts) so
    // an involuntary reload cannot lose it. draftStorageKey is the key the
    // current `draft` state was loaded for; saving is skipped until it matches
    // the current record's key, so one record's draft is never written under
    // another record's key while the record changes.
    const [draftStorageKey, setDraftStorageKey] = useState<string | null>(null);
    // True while the composer shows a draft restored from storage.
    const [draftRestored, setDraftRestored] = useState(false);
    // Reply target of a restored draft, applied once the comment list is loaded.
    const pendingReplyIdRef = useRef<string | null>(null);
    // When set, the composer posts a REPLY to this comment (its id becomes the
    // new comment's parent_comment_id); null means a new top-level comment.
    const [replyTo, setReplyTo] = useState<RecordCommentRow | null>(null);
    // Optional assignee for the comment being COMPOSED (id of an identity, or
    // null = unassigned). Sent as `assignee_identity_id` on the create mutation;
    // reset after a successful post and on record change. Slice 3 (assign-to-
    // identity): a comment can be directed at a specific identity so it surfaces
    // on that person's assigned-to-me view — mirrors the operator-task assignee.
    const [assigneeDraft, setAssigneeDraft] = useState<string | null>(null);
    // Space-saving layout (operator-task-fe-comment-panel-space-saving-collapse-
    // composer-and-compact-assign, Margot): the composer is collapsed behind an
    // "Add comment" button until the user opens it. Assigning happens on a posted
    // comment, from its header (CommentAssigneeControl).
    const [composerExpanded, setComposerExpanded] = useState(false);
    // When set, the comment with this id is being edited in place (own comments
    // only); editDraft holds the in-progress edited body. Editing another
    // person's comment is out of scope — the Edit affordance is shown only on
    // own comments. In-flight guard for the save request, kept separate from the
    // panel-wide isLoading so the Save button reflects submission precisely.
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editDraft, setEditDraft] = useState("");
    const [isSavingEdit, setSavingEdit] = useState(false);
    // The comment currently shown in the near-fullscreen reading overlay
    // (operator-task-fe-comment-fullscreen-expand), or null when the overlay is
    // closed.
    const [expandedComment, setExpandedComment] = useState<RecordCommentRow | null>(null);
    // The composer wrapper — scrolled into view + focused when a reply is armed.
    const composerRef = useRef<HTMLDivElement | null>(null);
    // The body an in-place edit started from, so an unchanged edit does not count
    // as an unsaved change (guards navigate-away). Set when an edit is armed.
    const editOriginalRef = useRef("");
    // The Save/Cancel action row of the currently-open edit box. Scrolled into
    // view when an edit is armed so the Save button is never hidden below the fold
    // / behind the sticky composer (operator-task-fe-comment-edit-scroll-save-
    // button-hidden; Margot 2026-09-24: "scroll down to save"). Only one edit box
    // is open at a time, so a single ref suffices.
    const editActionsRef = useRef<HTMLDivElement | null>(null);

    // A non-empty (non-whitespace) draft is a pending comment that would be lost
    // on navigate-away. Drives both the in-page tab-switch guard (via
    // setHasUnsavedChanges) and the route-change / beforeunload guard below.
    // An in-progress edit with unsaved changes (the draft diverges from the
    // original body) is likewise a pending change that must not be lost silently.
    const hasPendingComment = draft.trim().length > 0;
    const hasPendingEdit = hasUnsavedEdit(editingId, editDraft, editOriginalRef.current);
    const hasUnsavedChanges = hasPendingComment || hasPendingEdit;

    const userData = useSelector(getUserData);
    const currentIdentityId: string | undefined = userData?.identity_id;

    // ── Assignee picker source ──
    // The set of USERS that can be PICKED as a comment assignee, fetched once
    // (record-independent) into a shared dataStore key. A comment is directed at
    // a PERSON to answer, so the option set is human USER records only — never
    // service-users, roles/groups or other non-USER identities (confirmed by
    // Margot, 2026-09-23). A USER's id IS the identity id written to
    // `assignee_identity_id` (a USER is an identity subtype sharing its primary
    // key), so the row id is used directly — no id remap.
    //
    // This list is scoped to what the VIEWER can list (RBAC), so it is used ONLY
    // to populate the picker + as a secondary name fallback — it is NOT the
    // primary source for badge name RESOLUTION. The badge resolves the assignee
    // name from the per-comment `assignee_identity_id_identity { name }` relation
    // instead (see the comment query + assigneeLabel below), because that
    // server-side relation resolves names the viewer often CANNOT list here (e.g.
    // a founder USER that the Operator/a restricted viewer cannot enumerate —
    // grounded 2026-09-16: user_plain_list returns 0 rows for the Operator
    // session yet the per-comment relation resolves "stefan@…" fine, exactly like
    // the author relation).
    const usersKey = "commentAssigneeUsers";
    const getUsersSelector = (state) => state?.dataStore?.[usersKey]?.user_plain_list;
    const userRowsRaw: AssigneeUserRow[] | undefined = useSelector(getUsersSelector);
    // Stabilise the fallback so the useMemos below don't recompute every render
    // (the `?? []` would otherwise be a fresh array each time).
    const userRows = useMemo(() => userRowsRaw ?? [], [userRowsRaw]);

    useEffect(() => {
        new Promise((resolve, reject) => {
            dispatch({
                promise: { resolve, reject },
                type: dataStoreAction.START_FETCH_DATA,
                key: usersKey,
                params: {
                    // Scalars only ⇒ list cost is (0+1)×200 = 200, well within the
                    // query-complexity limit. 200 covers every realistic user
                    // roster; deleted users are filtered out client-side so a
                    // stale assignee still resolves its name but can't be picked.
                    query: `query {
                        user_plain_list(order:"+name",limit:200){ id name deleted_at }
                    }`,
                },
            });
        }).catch(() => undefined);
    }, [dispatch]);

    // Options for the picker exclude deleted users; the name map keeps ALL fetched
    // rows so an existing assignee pointing at a since-deleted user still renders
    // its name rather than a bare id. Pure builders live in commentAssignee.ts
    // (unit-tested).
    const userOptions = useMemo(() => buildAssigneeOptions(userRows), [userRows]);
    const userNameById = useMemo(() => buildAssigneeNameMap(userRows), [userRows]);

    // Human label for a comment's assignee: "you" for the current identity, else
    // the name resolved from the per-comment server-side relation (works even
    // when the viewer cannot list the user — RBAC), else the client-fetched
    // picker list, else a short id. Pure logic lives in commentAssignee.ts
    // (unit-tested); mirrors authorLabel's resolution order.
    const assigneeLabel = useCallback(
        (comment: RecordCommentRow): string =>
            resolveAssigneeName(comment, currentIdentityId, userNameById),
        [currentIdentityId, userNameById],
    );

    // How many of the most-recent comments to render. Bounds the initial DOM on
    // long threads; "Show older comments" reveals OLDER_PAGE_SIZE more each click.
    const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COMMENTS);
    // DOM refs for EVERY rendered comment, keyed by comment id (a ref-callback map
    // populated in renderComment). Lets the on-open anchor target an arbitrary
    // comment — the first UNREAD one — not only the newest/bottom-most.
    const commentRefs = useRef(new Map<string, HTMLDivElement>());
    // The comment the current scroll pass is anchoring to (read live by the
    // raf/ResizeObserver settle loop so it re-resolves against commentRefs as the
    // list grows). Set when a scroll is committed.
    const activeScrollTargetId = useRef<string | undefined>(undefined);
    // How to seat the active scroll target: "top" aligns its top just below the
    // header (reading downward from the first unread); "bottom" seats its bottom
    // above the composer (the newest-comment "jump to latest" resting place).
    const scrollAlignRef = useRef<"top" | "bottom">("bottom");
    // The scrolling comment-list container. Observed for late height changes so
    // the anchor can be re-applied as markdown/highlight settle.
    const commentListRef = useRef<HTMLDivElement | null>(null);
    // Armed on initial record load: "open" → anchor to the first unread comment,
    // or "deeplink" → a specific comment supplied via ?comment=<id> (see
    // commentPermalink.ts); and after the user posts ("post" → their just-posted
    // newest comment). A background refetch (refreshNonce) or a "show older"
    // expansion leaves it null so the scroll is NOT yanked. Consumed (→ null)
    // once applied.
    const scrollArm = useRef<"open" | "post" | "deeplink" | null>(
        deepLinkCommentId ? "deeplink" : "open",
    );
    // The specific comment id a "deeplink" arm anchors to (from ?comment=<id>).
    const deepLinkTargetId = useRef<string | undefined>(deepLinkCommentId);
    // Teardown for the in-flight deep-link settle (see startDeepLinkSettle). Held
    // in a ref — NOT returned as the arming effect's cleanup — so a re-render
    // while the tall value form resolves cannot tear the settle down mid-flight.
    const deepLinkSettleCleanup = useRef<null | (() => void)>(null);
    // Whether the viewer's read-until watermark fetch has settled. The on-open
    // "first unread" anchor must wait for it (else it would anchor to the oldest
    // comment while readUntil is still null and briefly mark everything unread).
    const [readStateLoaded, setReadStateLoaded] = useState(false);
    // The comment currently showing the transient on-arrival highlight (the first
    // unread the tab just scrolled to); cleared after a short window.
    const [highlightCommentId, setHighlightCommentId] = useState<string | undefined>(undefined);

    const getRecordCommentListSelector = (state) => state?.dataStore?.[key];
    const commentList: RecordCommentRow[] = useSelector(
        getRecordCommentListSelector,
    )?.record_comment_plain_list;

    // A non-empty draft or an in-progress edit counts as unsaved changes so
    // tab-switching is guarded.
    useEffect(() => {
        setHasUnsavedChanges(hasUnsavedChanges);
    }, [hasUnsavedChanges, setHasUnsavedChanges]);

    const getData = useCallback(() => {
        setLoading(true);
        // Re-arm the read-state gate: the on-open "first unread" anchor waits for
        // this watermark fetch to settle before deciding the scroll target.
        setReadStateLoaded(false);
        const filterStr = JSON.stringify(getSearchObj({ field: "record_id", value: id, op: "eq" }));
        // Fetch the viewer's read-until watermark for this record in parallel
        // with the comment list. Fail-closed-to-owner + UNIQUE(record, identity)
        // ⇒ ≤1 row; limit:1 keeps the query-cost validator happy ((1+1)×1). A
        // missing row (viewer never marked anything read here) leaves readUntil
        // null, so — per "literal B" — nothing is auto-read. Non-fatal on error.
        new Promise((resolve, reject) => {
            dispatch({
                promise: { resolve, reject },
                type: dataStoreAction.START_FETCH_DATA,
                key: watermarkKey,
                params: {
                    query: `query($filter: String) {
                        record_comment_watermark_plain_list(filter:$filter,limit:1){
                            id
                            read_until
                        },
                    }`,
                    variables: { filter: filterStr },
                },
            });
        })
            .then((data: any) => {
                const row = data?.record_comment_watermark_plain_list?.[0];
                setWatermarkId(row?.id ?? null);
                setReadUntil(row?.read_until ?? null);
            })
            .catch(() => undefined)
            // Settled either way (a missing watermark row legitimately leaves
            // readUntil null); unblock the on-open first-unread anchor.
            .finally(() => setReadStateLoaded(true));
        new Promise((resolve, reject) => {
            dispatch({
                promise: { resolve, reject },
                type: dataStoreAction.START_FETCH_DATA,
                key,
                params: {
                    query: `query($filter: String, $order: String, $limit: Int, $offset: Int) {
                        record_comment_plain_list(filter:$filter,order:$order,limit:$limit,offset:$offset){
                            id
                            body
                            author_identity_id
                            author_identity_id_identity { name }
                            assignee_identity_id
                            assignee_identity_id_identity { name }
                            parent_comment_id
                            resolved_at
                            resolved_by
                            created_at
                            edited_at
                            record_comment_read_plain_list_record_comment_id(limit: 1) { id is_read }
                        },
                    }`,
                    // `limit` bounds BOTH list fields for the backend query-cost
                    // validator (GRAPHQL_QUERY_COMPLEXITY_LIMIT, default 2000):
                    // every `_list` field costs (children+1)×limit, and an
                    // unresolved limit defaults to a 1000× factor. The inner
                    // read-marker list is bounded to 1 (the backend fails the
                    // select closed to the viewer's own marker, of which there is
                    // at most one per comment — see commentRead.ts), and the outer
                    // list to 500. With THREE per-comment child selections now
                    // (author-name relation, assignee-name relation, read-marker
                    // list) the query costs (3+1)×500 = 2000 — exactly the ceiling
                    // (empirically accepted; the validator rejects only ABOVE it).
                    // The assignee-name relation is resolved server-side per-comment
                    // (mirroring the author) BECAUSE the viewer often cannot list
                    // the assignee's USER identity client-side (RBAC): the picker's
                    // user_plain_list is scoped to what the viewer can list, so
                    // a USER assignee like a founder falls out of it and the badge
                    // would otherwise show a bare "Identity <id>". NOTE: at 2000/2000
                    // there is NO headroom — adding another per-comment relation
                    // will exceed the ceiling; drop the outer limit or split the
                    // fetch if a fourth is needed. 500 covers every realistic thread;
                    // true newest-first paging for larger threads is a follow-up.
                    variables: { filter: filterStr, order: "+created_at", offset: 0, limit: 500 },
                },
            });
        })
            .then((data: any) => {
                const count = data?.record_comment_plain_list?.length;
                dispatch({
                    type: resourcesActions.STORE_RESOURCE_DATA_WITH_PARTIAL_UPDATE,
                    iri: getIri(resourceName, id),
                    response: {
                        record_comment_list_record_id: {
                            record_comment_count: count,
                        },
                    },
                });
            })
            .catch((error) => error)
            .finally(() => setLoading(false));
    }, [dispatch, id, key, watermarkKey, resourceName]);

    // Re-fetch on mount / id change AND whenever the parent bumps refreshNonce
    // (manual record Refresh), so the list stays in sync with the badge count.
    useEffect(() => {
        if (id) {
            getData();
        }
    }, [getData, id, refreshNonce]);

    const migrateOnSaveParams = useMigrateOnSaveParams();
    const params = useMemo(
        () => ({ commit: false, ...migrateOnSaveParams }),
        [migrateOnSaveParams],
    );

    // Core post: returns the request promise and RE-THROWS on failure (after
    // surfacing the error) so callers that need to know the outcome — the
    // navigate-away Save action — can await it. The fire-and-forget wrapper
    // (`postComment`, used by the Comment button / Ctrl+Enter) swallows the
    // rejection so an unhandled promise rejection is never raised.
    const doPostComment = useCallback((): Promise<void> => {
        const body = draft.trim();
        // Re-entry guard: never fire a second create while one is already in
        // flight (a rapid double-click, or a future Ctrl+Enter path) — this is
        // what previously posted the same comment multiple times.
        if (!body || isSubmitting) {
            return Promise.resolve();
        }
        // A reply carries its target's id as parent_comment_id; a top-level
        // comment sends null. The backend `New_Record_Comment` input accepts
        // parent_comment_id (nullable) on the same create mutation.
        const parentCommentId = replyTo?.id ?? null;
        setSubmitting(true);
        return restClient
            .post(
                "/graphql",
                {
                    query: `mutation create_record_comment($record_id: String!, $body: String!, $parent_comment_id: String, $assignee_identity_id: String) {
                        create_record_comment(body: {record_id: $record_id, body: $body, parent_comment_id: $parent_comment_id, assignee_identity_id: $assignee_identity_id}) {
                            id
                        }
                    }`,
                    variables: {
                        record_id: id,
                        body,
                        parent_comment_id: parentCommentId,
                        // Optional: null when the composer leaves the comment
                        // unassigned (the common case).
                        assignee_identity_id: assigneeDraft,
                    },
                },
                { params },
            )
            .then(() => {
                setDraft("");
                setDraftRestored(false);
                setReplyTo(null);
                setAssigneeDraft(null);
                setComposerExpanded(false);
                // The freshly-posted comment is the newest — anchor to it once
                // the refetched list renders (NOT to the first unread: after
                // posting the user wants to see their own new comment).
                scrollArm.current = "post";
                getData();
            })
            .catch((error) => {
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="Sorry, couldn't post comment"
                        error={error}
                    />,
                );
                throw error;
            })
            .finally(() => setSubmitting(false));
    }, [draft, id, params, getData, isSubmitting, replyTo, assigneeDraft]);

    const postComment = useCallback(() => {
        doPostComment().catch(() => undefined);
    }, [doPostComment]);

    const handleDraftKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            // Ctrl+Enter / Cmd+Enter posts the comment — the conventional
            // submit-multiline-text shortcut. Plain Enter keeps its default
            // behaviour (newline in the multi-line composer). postComment
            // itself no-ops on an empty draft or an in-flight submit, so no
            // extra guarding is needed here; and a disabled composer never
            // receives keydown events.
            if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                postComment();
            }
        },
        [postComment],
    );

    const deleteComment = useCallback(
        (commentId: string) => {
            setLoading(true);
            // Return the promise so the confirm dialog's danger "Delete" button
            // stays in its in-flight (spinner + disabled) state and the dialog
            // stays open until the delete + refetch settle, rather than closing
            // instantly (ui-design-principle-buttons: async in-progress feedback).
            return restClient
                .post(
                    "/graphql",
                    {
                        query: `mutation delete_record_comment($id: String!){
                            delete_record_comment(id: $id)
                        }`,
                        variables: { id: commentId },
                    },
                    { params },
                )
                .then(() => getData())
                .catch((error) =>
                    NotificationService.error(
                        <RestErrorNotification
                            titleOverride="Sorry, couldn't delete comment"
                            error={error}
                        />,
                    ),
                )
                .finally(() => setLoading(false));
        },
        [params, getData],
    );

    // Comment delete is irreversible, and the delete control is a single click
    // right next to Reply/Edit — an accidental click silently loses a comment.
    // Interpose the app's shared confirmation modal (the same `showConfirmModal`
    // primitive used for every other destructive record action) before firing
    // the delete mutation: the danger-styled confirm affordance
    // (ui-design-principle-buttons: destructive actions use the danger button)
    // and the standard cancel keep it visually + behaviourally consistent, and
    // Cancel is a no-op that leaves the comment untouched.
    const confirmDeleteComment = useCallback(
        (commentId: string) => {
            showConfirmModal(buildDeleteCommentConfirm(() => deleteComment(commentId)));
        },
        [deleteComment],
    );

    // Cancel the new-comment composer: discard the in-progress draft and leave
    // any reply mode. Because discarding loses typed text it is guarded by the
    // shared confirmation modal when the draft holds real text (the same
    // data-safety guard the delete control uses); an empty draft clears with no
    // prompt. Mirrors cancelEdit for the edit box.
    const cancelComposer = useCallback(() => {
        const discard = () => {
            setDraft("");
            setDraftRestored(false);
            setReplyTo(null);
            setAssigneeDraft(null);
            setComposerExpanded(false);
        };
        if (shouldConfirmDiscardDraft(draft)) {
            showConfirmModal(buildDiscardCommentConfirm(discard));
        } else {
            discard();
        }
    }, [draft]);

    // Open the edit box on an OWN comment: seed the edit draft with the current
    // body and remember the original so an unchanged edit is not treated as
    // unsaved. Clears any armed reply so the two composers never fight.
    const openEditFor = useCallback((comment: RecordCommentRow) => {
        setReplyTo(null);
        setEditingId(comment.id);
        const original = comment.body ?? "";
        setEditDraft(original);
        editOriginalRef.current = original;
    }, []);

    const cancelEdit = useCallback(() => {
        setEditingId(null);
        setEditDraft("");
        editOriginalRef.current = "";
    }, []);

    // Core save of the in-place edit via the record_comment update mutation
    // (backend permits the author to update their own comment body; author/record
    // are immutable and edited_at is stamped server-side). Re-entry-guarded like
    // postComment; a body is required (NOT NULL), so an empty draft is rejected.
    // Returns the request promise and RE-THROWS on failure so the auto-save
    // transition and the navigate-away Save action can chain on the outcome; the
    // fire-and-forget wrapper (`saveEdit`) swallows the rejection.
    const doSaveEdit = useCallback((): Promise<void> => {
        if (!editingId) {
            return Promise.resolve();
        }
        const body = editDraft.trim();
        if (!body || isSavingEdit) {
            return Promise.resolve();
        }
        setSavingEdit(true);
        return restClient
            .post(
                "/graphql",
                {
                    query: `mutation update_record_comment($id: String!, $body: String!) {
                        update_record_comment(id: $id, body: {body: $body}) {
                            id
                        }
                    }`,
                    // Body only: the assignee is changed from the comment header
                    // (CommentAssigneeControl), so an edit never overwrites it.
                    variables: { id: editingId, body },
                },
                { params },
            )
            .then(() => {
                setEditingId(null);
                setEditDraft("");
                editOriginalRef.current = "";
                getData();
            })
            .catch((error) => {
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="Sorry, couldn't save the edit"
                        error={error}
                    />,
                );
                throw error;
            })
            .finally(() => setSavingEdit(false));
    }, [editingId, editDraft, isSavingEdit, params, getData]);

    // (Re)assign or unassign a POSTED comment from its header control. Writes only
    // `assignee_identity_id` (no body, so `edited_at` is not stamped); the backend
    // allows the author or anyone with write access to the record.
    const [assigningId, setAssigningId] = useState<string | null>(null);
    const assignComment = useCallback(
        (comment: RecordCommentRow, assigneeId: string | null) => {
            if (assigningId) {
                return;
            }
            setAssigningId(comment.id);
            restClient
                .post(
                    "/graphql",
                    {
                        query: `mutation update_record_comment($id: String!, $assignee_identity_id: String) {
                            update_record_comment(id: $id, body: {assignee_identity_id: $assignee_identity_id}) {
                                id
                            }
                        }`,
                        variables: { id: comment.id, assignee_identity_id: assigneeId },
                    },
                    { params },
                )
                .then(() => getData())
                .catch((error) => {
                    NotificationService.error(
                        <RestErrorNotification
                            titleOverride={
                                assigneeId
                                    ? "Sorry, couldn't assign the comment"
                                    : "Sorry, couldn't remove the assignment"
                            }
                            error={error}
                        />,
                    );
                })
                .finally(() => setAssigningId(null));
        },
        [assigningId, params, getData],
    );

    // Resolve / reopen an ASSIGNED comment (the assignee's checkmark). Writes only
    // `resolved_at`; the backend stamps `resolved_by` and lets the assignee do this
    // without write access to the record (commentResolve.ts).
    const [resolvingId, setResolvingId] = useState<string | null>(null);
    const toggleResolve = useCallback(
        (comment: RecordCommentRow) => {
            if (resolvingId) {
                return;
            }
            const action = resolveToggleAction(comment.resolved_at);
            setResolvingId(comment.id);
            restClient
                .post(
                    "/graphql",
                    {
                        query: `mutation update_record_comment($id: String!, $resolved_at: String) {
                            update_record_comment(id: $id, body: {resolved_at: $resolved_at}) {
                                id
                            }
                        }`,
                        variables: { id: comment.id, resolved_at: action.nextResolvedAt },
                    },
                    { params },
                )
                .then(() => getData())
                .catch((error) => {
                    NotificationService.error(
                        <RestErrorNotification
                            titleOverride={
                                action.nextResolvedAt
                                    ? "Sorry, couldn't mark the comment as done"
                                    : "Sorry, couldn't reopen the comment"
                            }
                            error={error}
                        />,
                    );
                })
                .finally(() => setResolvingId(null));
        },
        [resolvingId, params, getData],
    );

    const saveEdit = useCallback(() => {
        doSaveEdit().catch(() => undefined);
    }, [doSaveEdit]);

    // Enforce Margot's single-pending-item rule (2026-09-24): when the user moves
    // to a DIFFERENT comment input while an edit has unsaved changes, SAVE that
    // edit rather than silently dropping it. Used both when opening another
    // comment's edit (`startEdit`) and when focusing the new-comment composer
    // (`handleComposerFocus`). Returns a promise resolving to whether the caller
    // may proceed: `startEdit` waits for the save before opening the next editor,
    // and on save FAILURE (resolves false) keeps the in-flight edit visible rather
    // than clobbering it. Nothing to persist ("none" = no/unchanged edit, or
    // "discard" = a changed-but-empty edit that a NOT-NULL body cannot store)
    // resolves true immediately: no request fires and no savable content exists to
    // lose (startEdit then overwrites the empty editor; a composer focus leaves it
    // harmlessly open).
    const flushPendingEdit = useCallback((): Promise<boolean> => {
        const transition = resolvePendingEditTransition(
            editingId,
            editDraft,
            editOriginalRef.current,
        );
        if (transition === "save") {
            return doSaveEdit().then(
                () => true,
                () => false,
            );
        }
        return Promise.resolve(true);
    }, [editingId, editDraft, doSaveEdit]);

    // Post the new-comment composer draft when it holds unsaved content. Part of
    // the single-pending-item rule (Margot 2026-09-30): starting to edit a comment
    // while a new comment is half-written must SAVE (post) that new comment rather
    // than leave a second unsaved item that a later navigate-away could strand.
    // Resolves true when there is nothing to post or the post succeeded, false on
    // a post failure so the caller keeps the draft (and does not open the editor).
    const flushComposerDraft = useCallback((): Promise<boolean> => {
        if (draft.trim().length === 0) {
            return Promise.resolve(true);
        }
        return doPostComment().then(
            () => true,
            () => false,
        );
    }, [draft, doPostComment]);

    // Arm an in-place edit of an OWN comment. First flush EVERY other pending
    // input — a dirty in-place edit (save it) AND a half-written composer draft
    // (post it) — so opening this editor can never leave a second unsaved item
    // behind (Margot's "no two comments unsaved at once" rule, 2026-09-30). Only
    // open the new editor once both flushes succeed, so a save/post failure keeps
    // the in-flight content visible rather than clobbering it (the case-1
    // data-loss path Margot originally reported).
    const startEdit = useCallback(
        (comment: RecordCommentRow) => {
            Promise.all([flushPendingEdit(), flushComposerDraft()]).then(([editOk, composerOk]) => {
                if (editOk && composerOk) {
                    openEditFor(comment);
                }
            });
        },
        [flushPendingEdit, flushComposerDraft, openEditFor],
    );

    // Moving to the new-comment composer RESOLVES any open in-place editor so two
    // comments are never being edited at once (Margot 2026-09-30: "make sure no
    // two comments are ever edited at the same time"). A dirty edit is SAVED (the
    // moment the user turns to the composer, closing cases 2 & 3 — the pending
    // edit is committed before a new comment / record save + navigate-away can
    // strand it); an UNCHANGED open editor is CLOSED rather than left open to
    // later diverge into a second unsaved item (Margot's second constellation:
    // "closing any unchanged comments in edit mode as soon as I start writing a
    // new comment"). An emptied edit (NOT-NULL body → unsavable) is closed too.
    const handleComposerFocus = useCallback(() => {
        const action = editorActionOnComposerFocus(editingId, editDraft, editOriginalRef.current);
        if (action === "save") {
            void doSaveEdit();
        } else if (action === "close") {
            cancelEdit();
        }
    }, [editingId, editDraft, doSaveEdit, cancelEdit]);

    // Is there pending content the navigate-away prompt can actually SAVE? A
    // pending edit is savable only when non-empty (body is NOT NULL); a composer
    // draft is savable when non-empty (hasPendingComment already means that).
    // Only when true does the guard prompt offer a Save button — an empty edit
    // cannot be saved, so the prompt then offers only Go-back / Don't-save.
    const hasSavablePending = (hasPendingEdit && canSaveEdit(editDraft)) || hasPendingComment;

    // The Save action wired into the navigate-away guard prompt (Margot: "prompt
    // with save, as everywhere else in the application"). Persists EVERY pending
    // item — a dirty in-place edit AND/OR a half-written composer draft — so the
    // prompt can never save one while silently dropping the other (the data-loss
    // path Margot reported 2026-09-30, where the edit was saved but the unsaved
    // new comment was lost). The single-pending-item rule normally means only one
    // is present; saving both is the safe belt-and-suspenders. Runs on the shared
    // BlockingNavigationModal, and re-throws on any failure (Promise.all rejects)
    // so the modal keeps the prompt open instead of navigating away and losing
    // the change.
    const savePendingForNavAway = useCallback((): Promise<void> => {
        const tasks: Promise<void>[] = [];
        if (hasPendingEdit && canSaveEdit(editDraft)) {
            tasks.push(doSaveEdit());
        }
        if (hasPendingComment) {
            tasks.push(doPostComment());
        }
        return Promise.all(tasks).then(() => undefined);
    }, [hasPendingEdit, editDraft, hasPendingComment, doSaveEdit, doPostComment]);

    const handleEditKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            // Ctrl+Enter / Cmd+Enter saves the edit (mirrors the composer);
            // Escape cancels. saveEdit no-ops on an empty/in-flight draft.
            if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                saveEdit();
            } else if (event.key === "Escape") {
                event.preventDefault();
                cancelEdit();
            }
        },
        [saveEdit, cancelEdit],
    );

    const comments: RecordCommentRow[] = useMemo(() => commentList ?? [], [commentList]);

    // ── Per-identity read markers (unread dot + mark read / mark unread) ──
    // Margot's chosen model (2026-09-08) is "literal B": NOTHING auto-marks a
    // comment read. A comment from someone else stays unread until I explicitly
    // mark it read (its own toggle, or "Mark all as read"), and I can re-mark a
    // read comment unread to keep it on my "still to action" list — the
    // email-style read/unread behaviour she asked for. A comment I authored is
    // always "read by me" (you don't track unread on your own comments).
    const isReadByMe = useCallback(
        (comment: RecordCommentRow): boolean =>
            isCommentReadByMe(comment, currentIdentityId, readUntil),
        [currentIdentityId, readUntil],
    );

    // The oldest comment the viewer has not yet read — the on-open scroll anchor
    // (operator-task-fe-comments-tab-scroll-to-first-unread). `comments` is
    // chronological oldest→newest, so the first match is the oldest unread.
    const firstUnreadId = useMemo(
        () => firstUnreadCommentId(comments, currentIdentityId, readUntil),
        [comments, currentIdentityId, readUntil],
    );

    // id → comment lookup, used to walk a reply up to its top-level thread so a
    // first-unread comment living in a currently-hidden older thread can be
    // revealed (visibleCount bumped) before it is anchored.
    const commentById = useMemo(() => {
        const map = new Map<string, RecordCommentRow>();
        comments.forEach((comment) => map.set(comment.id, comment));
        return map;
    }, [comments]);
    const rootCommentIdOf = useCallback(
        (commentId?: string): string | undefined => {
            let cur = commentId ? commentById.get(commentId) : undefined;
            // Follow parent_comment_id up to the top-level root (bounded by the
            // finite thread; a missing/absent parent stops the walk).
            while (cur?.parent_comment_id && commentById.has(cur.parent_comment_id)) {
                cur = commentById.get(cur.parent_comment_id);
            }
            return cur?.id;
        },
        [commentById],
    );

    // Set a single comment's read state for the viewer. Overrides are kept
    // SPARSE (see commentRead.ts resolveOverrideAction): a per-comment override
    // row is created/updated only when the desired state diverges from the
    // record-level watermark, and removed once it agrees again — so the common
    // case (reading in step with the watermark) stores nothing per comment.
    const setCommentRead = useCallback(
        (comment: RecordCommentRow, desiredRead: boolean) => {
            const action = resolveOverrideAction(comment, desiredRead, readUntil);
            if (action.kind === "none") {
                return;
            }
            let request: Promise<unknown>;
            if (action.kind === "create") {
                request = restClient.post(
                    "/graphql",
                    {
                        query: `mutation set_read($record_comment_id: String!, $is_read: Boolean){
                            create_record_comment_read(body: {record_comment_id: $record_comment_id, is_read: $is_read}) { id }
                        }`,
                        variables: { record_comment_id: comment.id, is_read: action.isRead },
                    },
                    { params },
                );
            } else if (action.kind === "update") {
                request = restClient.post(
                    "/graphql",
                    {
                        query: `mutation set_read($id: String!, $is_read: Boolean){
                            update_record_comment_read(id: $id, body: {is_read: $is_read}) { id }
                        }`,
                        variables: { id: action.id, is_read: action.isRead },
                    },
                    { params },
                );
            } else {
                request = restClient.post(
                    "/graphql",
                    {
                        query: `mutation clear_read($id: String!){
                            delete_record_comment_read(id: $id)
                        }`,
                        variables: { id: action.id },
                    },
                    { params },
                );
            }
            setLoading(true);
            request
                .then(() => getData())
                .catch((error) =>
                    NotificationService.error(
                        <RestErrorNotification
                            titleOverride="Sorry, couldn't update the comment's read state"
                            error={error}
                        />,
                    ),
                )
                .finally(() => setLoading(false));
        },
        [params, getData, readUntil],
    );

    // Comments not yet read by me (excludes my own). Drives the unread dot,
    // the "N unread" count and the "Mark all as read" bulk action.
    const unreadComments: RecordCommentRow[] = useMemo(
        () => comments.filter((comment) => !isReadByMe(comment)),
        [comments, isReadByMe],
    );

    const markAllRead = useCallback(() => {
        if (unreadComments.length === 0) {
            return;
        }
        // Advance the watermark to the newest currently-fetched comment's SERVER
        // timestamp (not the browser clock — sidesteps client/server skew: every
        // visible comment is <= this, so all become watermark-read). Then drop
        // every per-comment override: with the watermark past them, is_read=false
        // re-flags would wrongly keep comments unread and is_read=true rows are
        // now redundant — clearing both keeps overrides sparse.
        const newestCreatedAt = comments[comments.length - 1]?.created_at;
        if (!newestCreatedAt) {
            return;
        }
        setLoading(true);
        const upsertWatermark = watermarkId
            ? restClient.post(
                  "/graphql",
                  {
                      query: `mutation set_watermark($id: String!, $read_until: String){
                          update_record_comment_watermark(id: $id, body: {read_until: $read_until}) { id }
                      }`,
                      variables: { id: watermarkId, read_until: newestCreatedAt },
                  },
                  { params },
              )
            : restClient.post(
                  "/graphql",
                  {
                      query: `mutation set_watermark($record_id: String!, $read_until: String){
                          create_record_comment_watermark(body: {record_id: $record_id, read_until: $read_until}) { id }
                      }`,
                      variables: { record_id: id, read_until: newestCreatedAt },
                  },
                  { params },
              );
        upsertWatermark
            .then(() => {
                const overrideIds = comments
                    .map((comment) => readOverride(comment)?.id)
                    .filter((oid): oid is string => !!oid);
                return Promise.all(
                    overrideIds.map((oid) =>
                        restClient.post(
                            "/graphql",
                            {
                                query: `mutation clear_read($id: String!){
                                    delete_record_comment_read(id: $id)
                                }`,
                                variables: { id: oid },
                            },
                            { params },
                        ),
                    ),
                );
            })
            .then(() => getData())
            .catch((error) =>
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="Sorry, couldn't mark all comments read"
                        error={error}
                    />,
                ),
            )
            .finally(() => setLoading(false));
    }, [unreadComments, comments, watermarkId, id, params, getData]);

    // Build the reply tree from the flat, chronologically-ordered list.
    // `parent_comment_id` groups replies under their parent; a comment whose
    // parent is null — or absent from the fetched set — is a top-level thread
    // (defensive: an orphan reply is surfaced rather than hidden). Insertion
    // order is preserved, so top-level threads and each reply group keep their
    // oldest→newest order (the app-wide comment convention).
    const { topLevel, childrenByParent } = useMemo(() => {
        const presentIds = new Set(comments.map((c) => c.id));
        const children = new Map<string, RecordCommentRow[]>();
        const roots: RecordCommentRow[] = [];
        for (const comment of comments) {
            const parentId = comment.parent_comment_id;
            if (parentId && presentIds.has(parentId)) {
                const siblings = children.get(parentId) ?? [];
                siblings.push(comment);
                children.set(parentId, siblings);
            } else {
                roots.push(comment);
            }
        }
        return { topLevel: roots, childrenByParent: children };
    }, [comments]);

    // View-layer paging bounds the number of top-level THREADS rendered (a
    // thread's replies always render with it). Chronological order preserved
    // (oldest→newest, newest thread at the bottom by the composer); older
    // threads stay one "Show older comments" click away. Preserves the exact
    // full-list fetch the comments-tab badge count rides on.
    const hiddenOlderCount = Math.max(0, topLevel.length - visibleCount);
    const visibleTopLevel = hiddenOlderCount > 0 ? topLevel.slice(hiddenOlderCount) : topLevel;

    // Switching records resets paging, clears any armed reply, and re-arms the
    // scroll-to-newest.
    useEffect(() => {
        setVisibleCount(INITIAL_VISIBLE_COMMENTS);
        setReplyTo(null);
        setAssigneeDraft(null);
        setComposerExpanded(false);
        setEditingId(null);
        setEditDraft("");
        editOriginalRef.current = "";
        // Re-arm the on-load scroll. A deep-link (?comment=<id>) anchors to a
        // SPECIFIC comment and must WIN over the default first-unread arm: this
        // reset effect runs on mount AFTER the scrollArm ref initializer, so an
        // unconditional "open" here would clobber the "deeplink" arm and the
        // target would never scroll/highlight (operator-task-fe-deep-link-to-comment).
        scrollArm.current = deepLinkCommentId ? "deeplink" : "open";
        deepLinkTargetId.current = deepLinkCommentId;
        setHighlightCommentId(undefined);
        // Clear the previous record's watermark so read-state never briefly
        // reflects the old record before getData refetches it for the new one;
        // re-arm the read-state gate so the first-unread anchor waits for it.
        setReadUntil(null);
        setWatermarkId(null);
        setReadStateLoaded(false);
        // deepLinkCommentId is intentionally read at id-change time only (its
        // value tracks the current record's ?comment): adding it to the deps
        // would re-run this reset when ResourcesSingle clears the consumed
        // param, yanking the view off the comment we just scrolled to.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // ── Durable composer draft ──
    // Load the stored draft when the record (or the viewing identity) changes.
    // Declared after the record-change reset above so it runs after it.
    const storageKey = currentIdentityId ? commentDraftKey(currentIdentityId, id) : null;
    useEffect(() => {
        if (!storageKey) return;
        pruneCommentDrafts();
        const stored = readCommentDraft(storageKey);
        setDraft(stored?.body ?? "");
        // The composer has no assign control; assigning happens on the posted comment.
        setAssigneeDraft(null);
        setDraftRestored(!!stored);
        pendingReplyIdRef.current = stored?.replyToId ?? null;
        setDraftStorageKey(storageKey);
    }, [storageKey]);

    // Save the draft on every change (an empty draft removes the entry).
    useEffect(() => {
        if (!storageKey || draftStorageKey !== storageKey) return;
        writeCommentDraft(storageKey, {
            body: draft,
            assigneeIdentityId: assigneeDraft,
            replyToId: replyTo?.id ?? pendingReplyIdRef.current,
        });
    }, [storageKey, draftStorageKey, draft, assigneeDraft, replyTo]);

    // Re-arm the reply target of a restored draft once the comments are loaded.
    // A target that no longer exists is dropped (the draft stays, top-level).
    const restoringReplyRef = useRef(false);
    useEffect(() => {
        const pendingId = pendingReplyIdRef.current;
        if (!pendingId || commentList === undefined || commentList === null) return;
        pendingReplyIdRef.current = null;
        const target = comments.find((comment) => comment.id === pendingId);
        if (target) {
            restoringReplyRef.current = true;
            setReplyTo(target);
        }
    }, [commentList, comments]);

    // When a reply is armed, bring the composer into view and focus it so the
    // user can type immediately after clicking "Reply" on a comment above.
    // A reply restored with a stored draft does not move the view.
    useEffect(() => {
        if (restoringReplyRef.current) {
            restoringReplyRef.current = false;
            return;
        }
        if (replyTo) {
            composerRef.current?.scrollIntoView({ block: "nearest" });
            composerRef.current?.querySelector("textarea")?.focus();
        }
    }, [replyTo]);

    const composerOpen = isComposerOpen({
        expanded: composerExpanded,
        draft,
        replying: !!replyTo,
        assigneeDraft,
    });

    // Opening the composer via "Add comment" focuses the text box so the user
    // can type immediately.
    useEffect(() => {
        if (composerExpanded) {
            composerRef.current?.querySelector("textarea")?.focus();
        }
    }, [composerExpanded]);

    // Single-shot: measure the newest comment against the live sticky occluders
    // and the composer, and scroll it fully into view within the record scroll
    // container. Seats the comment's bottom just above the composer, or its top
    // just below the header when the comment is taller than the visible band —
    // so the first line is never clipped (no overshoot). Pure apply (no defer);
    // the arming effect below controls WHEN this runs. See ./commentScroll.
    // Returns the pre-apply scroll delta that was needed to seat the target (0
    // when already within the readable band or there is nothing to scroll).
    const applyScrollToTarget = useCallback((): number => {
        const targetId = activeScrollTargetId.current;
        const el = targetId ? (commentRefs.current.get(targetId) ?? null) : null;
        if (!el) {
            return 0;
        }
        const scroller = getScrollParent(el);
        if (!scroller) {
            // No scrollable ancestor found — best-effort native fallback.
            el.scrollIntoView({ block: "nearest" });
            return 0;
        }
        const elRect = el.getBoundingClientRect();
        const scRect = scroller.getBoundingClientRect();
        // The sticky occluders pinned at the top of the record scroll view.
        // Two can be present: the record `.state-tabs` bar (ALWAYS sticky —
        // this is the tab row that carries the Comments tab), and the
        // `.page-header-sticky` page-header (breadcrumb + title + actions),
        // which the header only adds when it is BOTH sticky and actually
        // scroll-pinned (`isSticky && isScrollNeeded` in
        // ResourcesSinglePageHeader) — so it is frequently absent. We take
        // the LOWEST edge of whichever occluders are actually present so the
        // comment's first line is never clipped behind them, measured live so
        // pinned offsets / a taller header are accounted for without a magic
        // constant; falls back to the container top when none are present.
        const headerEls = [".state-tabs", ".page-header-sticky"]
            .map((sel) => document.querySelector<HTMLElement>(sel))
            .filter((el): el is HTMLElement => el !== null);
        const headerBottom = headerEls.length
            ? Math.max(...headerEls.map((el) => el.getBoundingClientRect().bottom))
            : scRect.top;
        const geometry = {
            elTop: elRect.top,
            elBottom: elRect.bottom,
            scTop: scRect.top,
            scBottom: scRect.bottom,
            composerHeight: composerRef.current?.offsetHeight ?? 0,
            headerBottom,
            gap: 8,
        };
        // "top" anchors the first-unread comment just below the header (read
        // downward from it); "bottom" seats the newest comment above the composer
        // (the "jump to latest" resting place).
        const delta =
            scrollAlignRef.current === "top"
                ? computeCommentTopAlignDelta(geometry)
                : computeCommentScrollDelta(geometry);
        if (Math.abs(delta) > 1) {
            scroller.scrollBy({ top: delta });
        }
        return delta;
    }, []);

    // Self-owned deep-link wait-then-scroll: poll the scroll container's content
    // height until the layout has gone quiescent (height unchanged for
    // DEEPLINK_SETTLE_QUIET_MS), a hard cap elapses, or the user makes a real
    // scroll gesture; then scroll the deep-linked comment into view EXACTLY ONCE
    // and reveal the on-arrival highlight. Returns a teardown fn. Deliberately
    // imperative + ref-owned so a React re-render (the value form / thread
    // resolving) does NOT tear it down mid-flight, and poll-based (not a
    // ResizeObserver) because a late Monaco-editor mount can reset the scroller
    // to the top with no size change an observer would miss. The comment is left
    // un-scrolled during the wait so it is never shown, pushed out of view by the
    // growing form, then chased back — the janky behaviour Stefan flagged.
    const startDeepLinkSettle = useCallback(
        (scroller: Element | null, targetId: string): (() => void) => {
            // Per-tick decision logic lives in ./commentDeepLinkSettle as a pure
            // state machine (unit-tested); this closure owns the imperative parts
            // (the poll interval, the gesture listeners, and the single
            // applyScrollToTarget + highlight once the layout is quiescent).
            let settleState = createDeepLinkSettleState(Date.now());
            let interval = 0;
            let scrolled = false;
            const abort = new AbortController();
            const stop = () => {
                window.clearInterval(interval);
                abort.abort();
            };
            const scrollOnce = () => {
                stop();
                scrolled = true;
                // Layout quiescent -> seat the target once, then reveal the
                // on-arrival highlight so the eye lands on the comment exactly as
                // it comes into view (not while it is still off-screen).
                applyScrollToTarget();
                setHighlightCommentId(targetId);
            };
            const tick = () => {
                // scrollHeight of the SAME record scroll container. While the value
                // form is still growing (late Monaco-editor mounts, async markdown)
                // this changes tick-to-tick; its stability is the "layout done" cue.
                const height = (scroller as HTMLElement | null)?.scrollHeight ?? 0;
                const result = stepDeepLinkSettle(settleState, height, Date.now(), {
                    quietMs: DEEPLINK_SETTLE_QUIET_MS,
                    hardCapMs: DEEPLINK_SETTLE_HARD_CAP_MS,
                });
                settleState = result.state;
                if (result.action === "stop") {
                    scrollOnce();
                }
            };
            // A genuine user scroll gesture during the wait means the user has
            // taken over — abandon the pending auto-scroll. Key off intent
            // gestures (wheel/touch/key), require isTrusted so a synthetic event
            // during layout/editor init cannot bail, and never off the `scroll`
            // event (our own single programmatic scroll fires it).
            const gestureTarget: EventTarget = scroller ?? window;
            const onGesture = (e: Event) => {
                if (!scrolled && shouldGestureStopSettle(e.isTrusted)) {
                    stop();
                }
            };
            gestureTarget.addEventListener("wheel", onGesture, {
                passive: true,
                signal: abort.signal,
            });
            gestureTarget.addEventListener("touchmove", onGesture, {
                passive: true,
                signal: abort.signal,
            });
            gestureTarget.addEventListener("keydown", onGesture, { signal: abort.signal });
            interval = window.setInterval(tick, DEEPLINK_SETTLE_POLL_MS);
            return stop;
        },
        [applyScrollToTarget, setHighlightCommentId],
    );

    // On an armed event, bring the anchor comment into view: on tab-open ("open")
    // the FIRST UNREAD comment (fallback: newest when all read); after posting
    // ("post") the just-posted newest comment. Keyed on the comment count, the
    // candidate target ids, the read-state gate and the paging bound so it fires
    // on initial load, after the watermark resolves, after a reveal, and after a
    // post — but the arm (consumed to null) means only an explicitly armed event
    // actually scrolls; a background refetch / "show older" never yanks the view.
    //
    // A SINGLE frame's measure races long threads: async markdown / syntax
    // highlighting / images keep GROWING the list AFTER the first frame, so a
    // one-shot scroll lands against a stale (shorter) height and undershoots
    // (measured live: the anchor comment left 4200px below the fold on a heavy
    // thread). So we apply once next frame and then RE-APPLY on every list
    // resize as the layout settles, bounded by SCROLL_SETTLE_WINDOW_MS so we
    // only correct during the brief settle window and never fight a user who
    // scrolls away afterwards.
    const newestCommentId = comments.length > 0 ? comments[comments.length - 1].id : undefined;
    useEffect(() => {
        const arm = scrollArm.current;
        if (!arm) {
            return;
        }
        // The on-open first-unread anchor must wait for the read-state watermark
        // (else it anchors to the oldest comment while readUntil is still null).
        if (arm === "open" && !readStateLoaded) {
            return;
        }
        // A deep-link arm anchors to a SPECIFIC comment (?comment=<id>). If that
        // id isn't in the fetched thread at all, surface a hint and consume the
        // arm (the first-unread/post arms always have a valid target by
        // construction, so this is the one genuine "target missing" case).
        if (arm === "deeplink") {
            const wantedId = deepLinkTargetId.current;
            if (!wantedId) {
                scrollArm.current = null;
                return;
            }
            // Wait for the fetch to resolve once before deciding it's missing
            // (comments is empty during the initial load).
            if (comments.length > 0 && !comments.some((comment) => comment.id === wantedId)) {
                scrollArm.current = null;
                NotificationService.warn("Comment not found — it may have been deleted.");
                return;
            }
        }
        const openUnreadId = arm === "open" ? firstUnreadId : undefined;
        const deepLinkId = arm === "deeplink" ? deepLinkTargetId.current : undefined;
        // First-unread and deep-linked targets both anchor at the TOP of the
        // readable band (read downward from them) and get the on-arrival
        // highlight; a newest/post target seats at the bottom ("jump to latest").
        const anchorTopId = openUnreadId ?? deepLinkId;
        const targetId = anchorTopId ?? newestCommentId;
        if (!targetId) {
            return;
        }
        // If the target lives in a currently-hidden older thread, reveal through
        // it first (its DOM ref exists only once rendered), then re-run.
        const rootIndex = topLevel.findIndex((thread) => thread.id === rootCommentIdOf(targetId));
        if (rootIndex >= 0 && rootIndex < hiddenOlderCount) {
            setVisibleCount((count) => Math.max(count, topLevel.length - rootIndex));
            return;
        }
        if (!commentRefs.current.get(targetId)) {
            // Ref not attached yet (first render for this target) — wait for the
            // next render without consuming the arm.
            return;
        }
        scrollArm.current = null;
        scrollAlignRef.current = anchorTopId ? "top" : "bottom";
        activeScrollTargetId.current = targetId;
        if (anchorTopId && arm !== "deeplink") {
            // Brief on-arrival highlight so the eye lands on the target comment.
            // A deep link reveals its highlight from startDeepLinkSettle at the
            // moment it scrolls the comment into view (after the layout has gone
            // quiescent), so it is not shown while the comment is still off-screen
            // during the wait.
            setHighlightCommentId(anchorTopId);
        }
        const targetEl = commentRefs.current.get(targetId) ?? null;
        const scroller = getScrollParent(targetEl);
        if (arm === "deeplink") {
            // The record opens cold: the value-form render + Monaco-editor mounts
            // keep growing the scroll container (and resetting it to the top) for
            // several seconds. Rather than seat the comment now and chase it as it
            // is displaced (which read as the comment flashing in, being pushed out
            // of view, then slowly re-scrolling — Stefan 2026-10-08), wait for the
            // layout to go quiescent and scroll it into view once. This wait is
            // owned by a ref and NOT returned as this effect's cleanup, so a
            // re-render during the long value-form render (the effect re-runs when
            // its `comments`/`topLevel` deps change) does not tear it down
            // mid-flight; it ends itself on quiescence / hard-cap / user gesture,
            // and on component unmount via a dedicated effect. Starting a new arm
            // cancels any prior in-flight wait first.
            deepLinkSettleCleanup.current?.();
            deepLinkSettleCleanup.current = startDeepLinkSettle(scroller, targetId);
            return;
        }
        let raf = requestAnimationFrame(applyScrollToTarget);
        const scheduleApply = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(applyScrollToTarget);
        };
        // Re-anchor as the layout settles: async markdown / syntax highlighting /
        // images keep growing comments around the target AFTER the first frame, so
        // we observe the comment LIST and re-apply on each resize.
        const observedEls = [commentListRef.current, scroller?.firstElementChild].filter(
            (el): el is Element => el != null,
        );
        let observer: ResizeObserver | null = null;
        if (observedEls.length && typeof ResizeObserver !== "undefined") {
            observer = new ResizeObserver(scheduleApply);
            observedEls.forEach((el) => observer?.observe(el));
        }
        // A genuine user scroll GESTURE ends the auto-anchor so the settle window
        // never fights a user who scrolls away. Key off intent gestures
        // (wheel / touch / key) — not the `scroll` event, which layout-driven
        // browser scroll-anchoring also fires — and require the event to be
        // user-originated (`isTrusted`) so a synthetic event during layout cannot
        // spuriously bail the settle.
        const gestureTarget: EventTarget = scroller ?? window;
        const abort = new AbortController();
        let stopAt = 0;
        const stop = () => {
            cancelAnimationFrame(raf);
            observer?.disconnect();
            observer = null;
            window.clearTimeout(stopAt);
            abort.abort();
        };
        const onGesture = (e: Event) => {
            if (e.isTrusted) {
                stop();
            }
        };
        gestureTarget.addEventListener("wheel", onGesture, {
            passive: true,
            signal: abort.signal,
        });
        gestureTarget.addEventListener("touchmove", onGesture, {
            passive: true,
            signal: abort.signal,
        });
        gestureTarget.addEventListener("keydown", onGesture, { signal: abort.signal });
        stopAt = window.setTimeout(stop, SCROLL_SETTLE_WINDOW_MS);
        return stop;
    }, [
        comments,
        firstUnreadId,
        newestCommentId,
        readStateLoaded,
        visibleCount,
        hiddenOlderCount,
        topLevel,
        rootCommentIdOf,
        applyScrollToTarget,
        startDeepLinkSettle,
    ]);

    // Tear down any in-flight deep-link settle when the panel unmounts (it is
    // intentionally ref-owned, not tied to the arming effect's cleanup, so it
    // survives re-renders during the long settle — see startDeepLinkSettle).
    useEffect(
        () => () => {
            deepLinkSettleCleanup.current?.();
            deepLinkSettleCleanup.current = null;
        },
        [],
    );

    // Clear the transient on-arrival highlight after a short window (the CSS fade
    // finishes well inside this; for reduced-motion users the static ring shows
    // for this window, then is removed).
    useEffect(() => {
        if (!highlightCommentId) {
            return;
        }
        const timer = window.setTimeout(() => setHighlightCommentId(undefined), 2000);
        return () => window.clearTimeout(timer);
    }, [highlightCommentId]);

    // Bring the Save/Cancel action row of the just-opened edit box into view so
    // the Save button is never hidden below the fold or behind the sticky
    // composer (operator-task-fe-comment-edit-scroll-save-button-hidden; Margot
    // 2026-09-24: "scroll down to save"). Reuses the same occluder-aware scroll
    // math as the newest-comment anchor (measuring the live sticky header +
    // composer), but scrolls ONLY when the action row is genuinely occluded — an
    // edit whose Save is already visible keeps the scroll position stable and does
    // not jump.
    const applyEditActionsScroll = useCallback(() => {
        const el = editActionsRef.current;
        if (!el) {
            return;
        }
        const scroller = getScrollParent(el);
        if (!scroller) {
            // No scrollable ancestor found — best-effort native fallback.
            el.scrollIntoView({ block: "nearest" });
            return;
        }
        const elRect = el.getBoundingClientRect();
        const scRect = scroller.getBoundingClientRect();
        // Lowest edge of whichever sticky top occluders are actually present (the
        // always-sticky `.state-tabs` tab bar, and the `.page-header-sticky`
        // header when scroll-pinned) — same live measurement as applyScrollToTarget.
        const headerEls = [".state-tabs", ".page-header-sticky"]
            .map((sel) => document.querySelector<HTMLElement>(sel))
            .filter((el): el is HTMLElement => el !== null);
        const headerBottom = headerEls.length
            ? Math.max(...headerEls.map((el) => el.getBoundingClientRect().bottom))
            : scRect.top;
        const composerHeight = composerRef.current?.offsetHeight ?? 0;
        const geometry = {
            elTop: elRect.top,
            elBottom: elRect.bottom,
            scTop: scRect.top,
            scBottom: scRect.bottom,
            composerHeight,
            headerBottom,
            gap: 8,
        };
        // Already fully visible between header and composer → leave scroll stable.
        if (isFullyWithinBand(geometry)) {
            return;
        }
        const delta = computeCommentScrollDelta(geometry);
        if (Math.abs(delta) > 1) {
            scroller.scrollBy({ top: delta });
        }
    }, []);

    // When an in-place edit is armed, scroll its Save action row into view next
    // frame (after the edit box — textarea + assignee row + actions — has laid
    // out, so its position is measurable). Keyed on editingId so it fires once
    // per opened editor and not on every keystroke.
    useEffect(() => {
        if (!editingId) {
            return;
        }
        const raf = requestAnimationFrame(applyEditActionsScroll);
        return () => cancelAnimationFrame(raf);
    }, [editingId, applyEditActionsScroll]);

    // In-app relative path that deep-links THIS comment: the record's own URL with
    // ?comment=<id> (see commentPermalink.ts). Used as the per-comment link's
    // `href` so the control is a REAL link — right-click "copy link address" yields
    // the full shareable URL, and middle/ctrl-click opens the record in a new tab
    // scrolled to the comment. Undefined only when the record name isn't resolved
    // yet (then the control falls back to a scroll-only button).
    const commentLinkPath = useCallback(
        (commentId: string): string | undefined =>
            resourceName ? buildCommentDeepLinkPath(resourceName, id, commentId) : undefined,
        [resourceName, id],
    );

    // Same-page scroll to an already-rendered comment + the brief on-arrival
    // highlight. Reuses the instant applyScrollToTarget primitive: the comment is
    // on screen and the layout is settled, so a single apply seats it — no
    // settle/chase machinery needed for an in-page jump.
    const scrollToComment = useCallback(
        (commentId: string) => {
            if (!commentRefs.current.get(commentId)) {
                return;
            }
            activeScrollTargetId.current = commentId;
            scrollAlignRef.current = "top";
            setHighlightCommentId(commentId);
            applyScrollToTarget();
        },
        [applyScrollToTarget],
    );

    // Click handler for the per-comment permalink. An unmodified left-click on the
    // SAME page must not navigate/reload — it should only scroll to the comment —
    // so we preventDefault (which also stops the react-router <Link> navigation)
    // and scroll in place. Modified / non-primary clicks (middle-click,
    // ctrl/cmd/shift/alt+click) are left to the browser so "open in a new tab" and
    // the context-menu "copy link address" work natively.
    const handleCommentLinkClick = useCallback(
        (e: ReactMouseEvent<HTMLElement>, commentId: string) => {
            if (!shouldScrollInPage(e)) {
                return;
            }
            e.preventDefault();
            scrollToComment(commentId);
        },
        [scrollToComment],
    );

    // Recursively render a comment and its reply subtree. `depth` drives the
    // visual indent, capped at MAX_THREAD_DEPTH so a long chain cannot run off
    // the right edge.
    const renderComment = (comment: RecordCommentRow, depth: number) => {
        const isOwn = canEditComment(comment, currentIdentityId);
        const isReplyTarget = replyTo?.id === comment.id;
        const isUnread = !isReadByMe(comment);
        const readToggle = readToggleAction(isUnread);
        const isEditing = editingId === comment.id;
        const isScrollHighlight = comment.id === highlightCommentId;
        const replies = childrenByParent.get(comment.id) ?? [];
        return (
            <div className={styles.commentThread} key={comment.id}>
                <div
                    className={`${styles.comment}${isOwn ? ` ${styles.commentOwn}` : ""}${
                        isReplyTarget ? ` ${styles.commentReplyTarget}` : ""
                    }${isUnread ? ` ${styles.commentUnread}` : ""}${
                        isScrollHighlight ? ` ${styles.commentScrollHighlight}` : ""
                    }`}
                    ref={(el) => {
                        // Register every comment's DOM node in the id→ref map so the
                        // on-open anchor can target an arbitrary comment (the first
                        // unread), not only the newest.
                        if (el) {
                            commentRefs.current.set(comment.id, el);
                        } else {
                            commentRefs.current.delete(comment.id);
                        }
                    }}
                >
                    <div className={styles.commentHeader}>
                        {/* Inert unread indicator (no click handler) — reads as
                            a status marker, not a control
                            (ui-design-principle-affordance-interactivity);
                            colour via a palette token
                            (ui-design-principle-color-palette). Always rendered
                            so its box is reserved: when read it is transparent,
                            and marking read causes no layout shift. */}
                        <span
                            className={`${styles.unreadDot}${
                                isUnread ? "" : ` ${styles.unreadDotRead}`
                            }`}
                            {...unreadDotA11yProps(isUnread)}
                        />
                        <span
                            className={`${styles.author}${
                                isUnread ? ` ${styles.authorUnread}` : ""
                            }`}
                            data-text={authorLabel(comment, currentIdentityId)}
                        >
                            {authorLabel(comment, currentIdentityId)}
                        </span>
                        <span className={styles.timestamp}>
                            <TimeAgoWithDateTimeFormatter datetime={comment.created_at} />
                        </span>
                        {comment.edited_at && (
                            // Subtle "edited" provenance marker with the last-edit
                            // time (hover shows the exact datetime). Inert display
                            // text, not a control (ui-design-principle-affordance-
                            // interactivity); muted via a palette token.
                            <span className={styles.editedMarker}>
                                · edited{" "}
                                <TimeAgoWithDateTimeFormatter datetime={comment.edited_at} />
                            </span>
                        )}
                        {/* Assignee chip / "Assign" button: opens the user
                            picker right under it (Margot 2026-11-03). */}
                        <CommentAssigneeControl
                            commentId={comment.id}
                            assigneeId={comment.assignee_identity_id}
                            assigneeLabel={assigneeLabel(comment)}
                            resolved={!!comment.resolved_at}
                            variant={assigneeBadgeVariant(comment, currentIdentityId)}
                            userOptions={userOptions}
                            canAssign={!isReadOnly && !isDisabled}
                            saving={assigningId === comment.id}
                            onAssign={(assigneeId) => assignComment(comment, assigneeId)}
                        />
                        {canToggleResolve(comment, currentIdentityId, !isDisabled, isReadOnly) &&
                            (() => {
                                // The assignee's checkmark next to the badge: marks
                                // the assigned comment done, or reopens it.
                                const action = resolveToggleAction(comment.resolved_at);
                                return (
                                    <Button
                                        icon={action.icon}
                                        variation="icon"
                                        title={action.title}
                                        ariaLabel={action.ariaLabel}
                                        onClick={() => toggleResolve(comment)}
                                        loading={resolvingId === comment.id}
                                        labelOnlyAria
                                    />
                                );
                            })()}
                        {/*
                         * Secondary per-comment actions — the permalink and the
                         * (rare, destructive) delete — live top-right of the header
                         * as plain icon buttons, matching every other icon button in
                         * the UI (standard `icon` variant, no comment-specific
                         * recolour) per Margot's 2026-10-20 direction on
                         * operator-task-fe-comment-actions-position-bottom-left. The
                         * frequent conversational actions (Reply / Edit / Mark-read)
                         * live instead in the bottom-left footer below the body. The
                         * group is pushed to the right edge by the header's flex gap
                         * + margin-left:auto (.commentHeaderActions).
                         */}
                        <div className={styles.commentHeaderActions}>
                            {/*
                             * Permalink to THIS comment — a real LINK, not a button,
                             * so it is middle-clickable (open the record in a new tab,
                             * scrolled to the comment) and right-clickable ("copy link
                             * address"): an action representable by a URL should be a
                             * link (ui-design-principle-buttons /
                             * -affordance-interactivity). An unmodified left-click on
                             * the same page only scrolls to the comment
                             * (handleCommentLinkClick). Needs no write permission, so
                             * it is never disabled.
                             */}
                            <Button
                                icon={IconCollection.link}
                                variation="icon"
                                href={commentLinkPath(comment.id)}
                                onClick={(e) => handleCommentLinkClick(e, comment.id)}
                                ariaLabel="Link to this comment"
                                title="Link to this comment — click to scroll here, middle-click to open in a new tab, right-click to copy the URL"
                                labelOnlyAria
                            />
                            {!isReadOnly && (
                                <Button
                                    icon={IconCollection.deleteOutline}
                                    variation="icon"
                                    title="Delete comment"
                                    ariaLabel="Delete comment"
                                    onClick={() => confirmDeleteComment(comment.id)}
                                    disabled={isDisabled}
                                    labelOnlyAria
                                />
                            )}
                        </div>
                    </div>
                    {isEditing ? (
                        <div className={styles.editBox}>
                            <Input.TextArea
                                value={editDraft}
                                onChange={(event) => setEditDraft(event.target.value)}
                                onKeyDown={handleEditKeyDown}
                                autoSize={{ minRows: 3, maxRows: 12 }}
                                disabled={isDisabled || isSavingEdit}
                                autoFocus
                            />
                            <div className={styles.editActions} ref={editActionsRef}>
                                <Button
                                    label="Save"
                                    title="Save edit (Ctrl+Enter / ⌘+Enter)"
                                    onClick={saveEdit}
                                    variation="primary"
                                    size="small"
                                    loading={isSavingEdit}
                                    disabled={!canSaveEdit(editDraft) || isDisabled}
                                />
                                <Button
                                    label="Cancel"
                                    title="Cancel edit (Esc)"
                                    onClick={cancelEdit}
                                    variation="white"
                                    size="small"
                                    disabled={isSavingEdit}
                                />
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className={styles.commentBodyWrap}>
                                {isExpandableComment(comment.body) && (
                                    // Expand-to-fullscreen affordance, revealed on
                                    // hover of the comment (and on keyboard focus),
                                    // mirroring the code editor's top-right maximize
                                    // handle (Margot's cited reference). Quiet
                                    // icon-grey hover-reveal per
                                    // ui-design-principle-inline-hover-actions; shown
                                    // only on long comments where the limited inline
                                    // scroll window is a real constraint. A pure view
                                    // action — available even when writing is disabled
                                    // (read-only users benefit most from reading long
                                    // comments).
                                    <div className={styles.expandHandle}>
                                        <Button
                                            icon={IconCollection.arrowExpand}
                                            variation="icon"
                                            title="Expand to fullscreen"
                                            onClick={() => setExpandedComment(comment)}
                                        />
                                    </div>
                                )}
                                <Markdown
                                    markdownString={comment.body ?? ""}
                                    canCopy={false}
                                    className={styles.commentBody}
                                />
                            </div>
                            {/*
                             * Per-comment conversational action footer, at the
                             * comment's BOTTOM-LEFT below the body so "mark read" /
                             * "reply" sit where the eye lands when reading finishes —
                             * a natural read-then-mark flow
                             * (operator-task-fe-comment-actions-position-bottom-left,
                             * Margot). Per Margot's 2026-10-20 direction, the frequent
                             * actions (Reply, Edit, Mark-read/unread) are labelled
                             * buttons WITH icons — labels restore discoverability over
                             * icon-only controls — rendered low-emphasis (the `white`
                             * secondary variant + small size, matching the panel's
                             * "Mark all as read" bulk button; ui-design-principle-
                             * buttons). A divider (.commentFooter border-top) visually
                             * separates the footer from the comment text. The rare
                             * secondary actions (permalink, delete) live instead as
                             * plain icon buttons top-right of the header. Hidden while
                             * editing — the Save/Cancel controls own the row then.
                             */}
                            <div className={styles.commentFooter}>
                                {/*
                                 * Read/unread toggle — shown only on comments authored
                                 * by someone else: you don't track unread on your own.
                                 * Icon + label come from readToggleAction so the
                                 * state→label/icon mapping is unit-tested.
                                 */}
                                {!isOwn && (
                                    <Button
                                        icon={readToggle.icon}
                                        variation="white"
                                        size="small"
                                        label={readToggle.title}
                                        title={readToggle.title}
                                        ariaLabel={readToggle.ariaLabel}
                                        onClick={() => setCommentRead(comment, readToggle.nextRead)}
                                        disabled={isDisabled && !isReadOnly}
                                    />
                                )}
                                {/* Edit — own comments only. */}
                                {isOwn && !isReadOnly && (
                                    <Button
                                        icon={IconCollection.pencilOutline}
                                        variation="white"
                                        size="small"
                                        label="Edit"
                                        title="Edit"
                                        ariaLabel="Edit comment"
                                        onClick={() => startEdit(comment)}
                                        disabled={isDisabled}
                                    />
                                )}
                                {!isReadOnly && (
                                    <Button
                                        icon={IconCollection.arrowUpLeft}
                                        variation="white"
                                        size="small"
                                        label="Reply"
                                        title="Reply"
                                        ariaLabel="Reply"
                                        onClick={() => setReplyTo(comment)}
                                        disabled={isDisabled}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>
                {replies.length > 0 && (
                    <div className={depth < MAX_THREAD_DEPTH ? styles.commentChildren : undefined}>
                        {replies.map((reply) =>
                            renderComment(reply, Math.min(depth + 1, MAX_THREAD_DEPTH)),
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={styles.commentsPanel}>
            {/*
             * Guard the pending comment against navigate-away (in-app route
             * change + browser tab close/refresh via beforeunload). Reuses the
             * shared unsaved-changes machinery — the same BlockingNavigationModal
             * the parent record form and the metadata tab use — rather than a
             * parallel mechanism. An empty/whitespace-only composer does not
             * block (hasPendingComment is false).
             */}
            <BlockingNavigationModal
                navigate={history.push}
                shouldBlockNavigation={hasUnsavedChanges}
                titleText={strings.navigateAwayUnsavedCommentPrompt}
                priority={NAVIGATION_PRIORITIES.COMMENT_FORM}
                allowNavigation={allowNavigation}
                // Offer a Save action alongside Go-back / Don't-save — only when
                // the pending item is actually savable (an empty edit is not).
                submitForm={hasSavablePending ? savePendingForNavAway : undefined}
                isSubmitDisabled={isSubmitting || isSavingEdit}
            />
            <SpinnerAnimation isLoading={isLoading} className={styles.spinnerAnimation} />
            {comments.length === 0 && !isLoading ? (
                <div className={styles.emptyState}>No comments yet.</div>
            ) : (
                <div className={styles.commentList} ref={commentListRef}>
                    {hiddenOlderCount > 0 && (
                        <Button
                            className={styles.showOlder}
                            variation="white"
                            size="small"
                            label={`Show older comments (${hiddenOlderCount} more)`}
                            onClick={() => setVisibleCount((count) => count + OLDER_PAGE_SIZE)}
                        />
                    )}
                    {visibleTopLevel.map((comment) => renderComment(comment, 0))}
                </div>
            )}
            <div className={styles.newComment} ref={composerRef}>
                {unreadComments.length > 0 && (
                    // Bulk "mark all as read" + unread count, kept in the sticky
                    // composer so it is always reachable regardless of scroll.
                    <div className={styles.unreadToolbar}>
                        <span className={styles.unreadCount}>
                            {unreadComments.length}{" "}
                            {unreadComments.length === 1 ? "unread comment" : "unread comments"}
                        </span>
                        <Button
                            variation="white"
                            size="small"
                            label="Mark all as read"
                            onClick={markAllRead}
                            disabled={isDisabled && !isReadOnly}
                        />
                    </div>
                )}
                {isReadOnly ? (
                    <div className={styles.readOnlyNote}>
                        This record is read-only, so its comments cannot be added, changed, resolved
                        or deleted.
                    </div>
                ) : composerOpen ? (
                    <>
                        {draftRestored && draft.trim().length > 0 && (
                            <div className={styles.restoredDraftNote}>
                                Your unsaved comment was restored. Post it, or use Cancel to discard
                                it.
                            </div>
                        )}
                        {replyTo && (
                            <div className={styles.replyingTo}>
                                <span className={styles.replyingToLabel}>
                                    Replying to {authorLabel(replyTo, currentIdentityId)}
                                </span>
                                <Button
                                    variation="white"
                                    size="small"
                                    label="Cancel"
                                    onClick={() => setReplyTo(null)}
                                    disabled={isSubmitting}
                                />
                            </div>
                        )}
                        <Input.TextArea
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            onKeyDown={handleDraftKeyDown}
                            onFocus={handleComposerFocus}
                            placeholder={
                                replyTo
                                    ? "Write a reply (Markdown supported)…"
                                    : "Write a comment (Markdown supported)…"
                            }
                            autoSize={{ minRows: 3, maxRows: 12 }}
                            disabled={composerLocked}
                        />
                        <div className={styles.newCommentActions}>
                            <Button
                                label={replyTo ? "Reply" : "Comment"}
                                title={
                                    replyTo
                                        ? "Post reply (Ctrl+Enter / ⌘+Enter)"
                                        : "Post comment (Ctrl+Enter / ⌘+Enter)"
                                }
                                onClick={postComment}
                                variation="primary"
                                size="small"
                                loading={isSubmitting}
                                disabled={draft.trim().length === 0 || composerLocked}
                            />
                            {/*
                             * Cancel the in-progress comment: the secondary peer of the
                             * blue `primary` Save (ui-design-principle-buttons). Discards
                             * the draft (guarded by a confirm when it holds text). Enabled
                             * whenever there is something to cancel — a typed draft or an
                             * armed reply.
                             */}
                            <Button
                                label="Cancel"
                                title="Discard this comment"
                                onClick={cancelComposer}
                                variation="white"
                                size="small"
                                disabled={composerLocked}
                            />
                            <DocsInfoTooltip
                                title="Record comments"
                                urlPath="/docs/accessing-and-manipulating-records#record-comments"
                            />
                        </div>
                    </>
                ) : (
                    // Collapsed: only the "Add comment" button, so the reading
                    // area keeps the vertical space the full composer took.
                    <div className={styles.newCommentActions}>
                        <Button
                            label="Add comment"
                            title="Write a new comment"
                            icon={IconCollection.plus}
                            onClick={() => setComposerExpanded(true)}
                            variation="dark"
                            size="small"
                            disabled={isDisabled}
                        />
                        <DocsInfoTooltip
                            title="Record comments"
                            urlPath="/docs/accessing-and-manipulating-records#record-comments"
                        />
                    </div>
                )}
            </div>
            {/*
             * Near-fullscreen reading overlay for a long comment, opened from the
             * per-comment expand affordance (operator-task-fe-comment-fullscreen-
             * expand). Rendered once for the panel and driven by state so only a
             * single overlay instance exists regardless of thread size.
             */}
            <CommentFullscreenModal
                isVisible={!!expandedComment}
                onCancel={() => setExpandedComment(null)}
                body={expandedComment?.body ?? ""}
                header={
                    expandedComment ? (
                        <>
                            {authorLabel(expandedComment, currentIdentityId)}
                            {" · "}
                            <TimeAgoWithDateTimeFormatter datetime={expandedComment.created_at} />
                        </>
                    ) : undefined
                }
            />
        </div>
    );
};
